import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSlug from 'rehype-slug';
import rehypeRaw from 'rehype-raw';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, prism } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useAuth } from '../../context/AuthContext';
import { getDocumentationPost, getDocumentationPostByRoute, deleteDocumentationPost } from '../../api/documentation';
import DocumentationPostDialog from './DocumentationPostDialog';
import { extractHeadings, scrollToElement, generateHeadingId } from './Markdown';
import { createMarkdownComponents } from '../common/MarkdownComponents';

// Icons as SVG components
const BackIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

const EditIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const DeleteIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const CopyIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const TocIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h7" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
  </svg>
);

// ========================
// Post Header Component
// ========================
const PostHeader = ({ post, isAdmin, onEdit, onDeleteClick, formattedDate }) => {
  return (
    <>
      <div className="flex justify-between items-start gap-2 w-full max-w-full overflow-hidden">
        <div className="flex-1 min-w-0">
          <h1 className="text-3xl font-bold mb-2 text-ui-light-text-primary dark:text-ui-dark-text-primary break-words">
            {post.title}
          </h1>

          <p className="text-sm text-ui-light-text-secondary dark:text-ui-dark-text-tertiary mb-3 break-words">
            {post.author_name ? `Posted by ${post.author_name} • ` : ''}
            {formattedDate}
          </p>

          {!!post.roles?.length && (
            <div className="flex flex-wrap gap-2 mb-6">
              {post.roles.map(role => (
                <span
                  key={role.id}
                  className="px-3 py-1 text-xs font-medium rounded-full bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200"
                >
                  {role.name}
                </span>
              ))}
            </div>
          )}
        </div>

        {isAdmin && (
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={onEdit}
              className="p-2 rounded-lg text-primary-600 dark:text-primary-400 hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors"
              aria-label="Edit post"
            >
              <EditIcon />
            </button>
            <button
              onClick={onDeleteClick}
              className="p-2 rounded-lg text-status-error-light-text dark:text-status-error-dark-text hover:bg-status-error-light-bg dark:hover:bg-status-error-dark-bg transition-colors"
              aria-label="Delete post"
            >
              <DeleteIcon />
            </button>
          </div>
        )}
      </div>

      <hr className="my-6 border-ui-light-border dark:border-ui-dark-border" />
    </>
  );
};

// ========================
// Table of Contents Component
// ========================
const TableOfContents = ({ headings, onItemClick }) => {
  const [expandedSections, setExpandedSections] = useState(new Set());

  useEffect(() => {
    if (headings && headings.length > 0) {
      const level1HeadingIds = headings.filter(h => h.level === 1).map(h => h.id);
      setExpandedSections(new Set(level1HeadingIds));
    }
  }, [headings]);

  if (!headings || headings.length === 0) return null;

  const groupedHeadings = [];
  let currentGroup = null;
  headings.forEach((heading) => {
    if (heading.level === 1) {
      currentGroup = { mainHeading: heading, subHeadings: [] };
      groupedHeadings.push(currentGroup);
    } else if (currentGroup) {
      currentGroup.subHeadings.push(heading);
    } else {
      if (groupedHeadings.length === 0) {
        currentGroup = { mainHeading: null, subHeadings: [] };
        groupedHeadings.push(currentGroup);
      }
      groupedHeadings[0].subHeadings.push(heading);
    }
  });

  const toggleExpansion = (headingId) => {
    const next = new Set(expandedSections);
    next.has(headingId) ? next.delete(headingId) : next.add(headingId);
    setExpandedSections(next);
  };

  return (
    <div className="w-full max-w-full bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-sm border border-ui-light-border dark:border-ui-dark-border overflow-hidden">
      <div className="flex items-center gap-2 p-4 border-b border-ui-light-border dark:border-ui-dark-border">
        <TocIcon />
        <h2 className="text-lg font-semibold text-ui-light-text-primary dark:text-ui-dark-text-primary">Contents</h2>
      </div>
      <div className="p-2">
        {groupedHeadings.map((group, groupIndex) => (
          <div key={groupIndex}>
            {group.mainHeading && (
              <div
                onClick={() => {
                  onItemClick(group.mainHeading.id);
                  if (group.subHeadings.length > 0) toggleExpansion(group.mainHeading.id);
                }}
                className="flex items-center justify-between px-3 py-2 cursor-pointer rounded-lg hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors relative"
              >
                <div className="absolute left-0 top-1/2 -translate-y-1/2 h-3/5 w-1 bg-primary-600 dark:bg-primary-400 rounded-r"></div>
                <span className="text-sm font-medium text-primary-600 dark:text-primary-400 truncate ml-2">
                  {group.mainHeading.text}
                </span>
                {group.subHeadings.length > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpansion(group.mainHeading.id);
                    }}
                    className="p-1 rounded transition-transform flex-shrink-0"
                    style={{
                      transform: expandedSections.has(group.mainHeading.id) ? 'rotate(90deg)' : 'rotate(0deg)'
                    }}
                  >
                    <ChevronRightIcon />
                  </button>
                )}
              </div>
            )}

            {(!group.mainHeading || expandedSections.has(group.mainHeading?.id)) &&
              group.subHeadings.map((heading, index) => (
                <div
                  key={`${groupIndex}-${index}`}
                  onClick={() => onItemClick(heading.id)}
                  className={`px-3 py-2 cursor-pointer rounded-lg hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-all ${
                    group.mainHeading ? 'ml-4' : ''
                  }`}
                  style={{ paddingLeft: `${heading.level * 1.5}rem` }}
                >
                  <span className="text-sm text-ui-light-text-primary dark:text-ui-dark-text-primary truncate block">
                    {heading.text}
                  </span>
                </div>
              ))
            }
          </div>
        ))}
      </div>
    </div>
  );
};

// ========================
// Heading Renderer with Copy Link
// ========================
const HeadingRenderer = ({ level, children }) => {
  const id = generateHeadingId(children.toString());
  const [copied, setCopied] = useState(false);

  const handleCopyLink = (e) => {
    e.preventDefault();
    const url = new URL(window.location.href);
    url.hash = id;
    navigator.clipboard.writeText(url.toString());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const Tag = `h${level}`;
  const sizeClasses = {
    1: 'text-3xl',
    2: 'text-2xl',
    3: 'text-xl',
    4: 'text-lg',
    5: 'text-base',
    6: 'text-sm'
  };

  const marginClasses = level === 1 ? 'mt-8 mb-4' : 'mt-6 mb-2';

  return (
    <Tag id={id} className={`${sizeClasses[level]} font-bold ${marginClasses} text-ui-light-text-primary dark:text-ui-dark-text-primary break-words group`}>
      <div className="flex items-center gap-2">
        <span>{children}</span>
        <button
          onClick={handleCopyLink}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover"
          title={copied ? "Link copied!" : "Copy link to section"}
          aria-label="Copy link to section"
        >
          <CopyIcon />
        </button>
      </div>
    </Tag>
  );
};

// Override headings to add copy link functionality
const createDocumentationMarkdownComponents = (isDarkMode) => {
  const baseComponents = createMarkdownComponents(isDarkMode);

  return {
    ...baseComponents,
    h1: props => <HeadingRenderer level={1} {...props} />,
    h2: props => <HeadingRenderer level={2} {...props} />,
    h3: props => <HeadingRenderer level={3} {...props} />,
    h4: props => <HeadingRenderer level={4} {...props} />,
    h5: props => <HeadingRenderer level={5} {...props} />,
    h6: props => <HeadingRenderer level={6} {...props} />
  };
};

// ========================
// Loading / Error Views
// ========================
const LoadingView = () => {
  return (
    <div className="box-border w-full max-w-full px-4 sm:px-6 md:px-8 py-8">
      <div className="flex justify-center items-center min-h-[60vh]">
        <svg
          className="w-12 h-12 text-primary-600 dark:text-primary-400 animate-spin"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </div>
    </div>
  );
};

const ErrorView = ({ error, navigateBack }) => {
  return (
    <div className="box-border w-full max-w-full px-4 sm:px-6 md:px-8 py-8 overflow-x-hidden">
      <button
        onClick={navigateBack}
        className="flex items-center gap-2 mb-8 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
      >
        <BackIcon />
        <span>Back to Documentation</span>
      </button>
      <div className="p-4 rounded-lg bg-status-error-light-bg dark:bg-status-error-dark-bg text-status-error-light-text dark:text-status-error-dark-text">
        {error || 'Documentation post not found'}
      </div>
    </div>
  );
};

// ========================
// Delete Confirmation Dialog
// ========================
const DeleteConfirmationDialog = ({ open, onClose, onConfirm }) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-xl font-bold mb-4 text-ui-light-text-primary dark:text-ui-dark-text-primary">
          Confirm Deletion
        </h2>
        <p className="mb-6 text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">
          Are you sure you want to delete this documentation post? This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-ui-light-text-primary dark:text-ui-dark-text-primary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 rounded-lg bg-status-error-light-bg dark:bg-status-error-dark-bg text-status-error-light-text dark:text-status-error-dark-text hover:opacity-90 transition-opacity"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// ========================
// MAIN COMPONENT
// ========================
const DocumentationDetail = () => {
  const { postId, routeName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [headings, setHeadings] = useState([]);
  const [isDesktopTocPinned, setIsDesktopTocPinned] = useState(false);
  const contentRef = useRef(null);
  const desktopTocRef = useRef(null);

  const isAdmin = user?.is_staff || user?.is_superuser;
  const isDarkMode = document.documentElement.classList.contains('dark');

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        setError(null);
        let data;
        if (postId && !isNaN(postId)) data = await getDocumentationPost(postId);
        else if (routeName) data = await getDocumentationPostByRoute(routeName);
        else if (postId) data = await getDocumentationPostByRoute(postId);
        else throw new Error('No post identifier provided');

        setPost(data);
        if (data?.content) setHeadings(extractHeadings(data.content));
      } catch (err) {
        console.error('Error fetching documentation post:', err);
        setError(err.response?.data?.detail || err.message || 'Failed to load the documentation post');
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [postId, routeName]);

  useEffect(() => {
    if (!loading && location.hash && contentRef.current) {
      const id = location.hash.substring(1);
      setTimeout(() => scrollToElement(id), 100);
    }
  }, [loading, location.hash, post]);

  useEffect(() => {
    if (!headings.length) {
      setIsDesktopTocPinned(false);
      return undefined;
    }

    const desktopBreakpoint = window.matchMedia('(min-width: 1024px)');

    const updatePinnedState = () => {
      if (!desktopBreakpoint.matches || !desktopTocRef.current) {
        setIsDesktopTocPinned(false);
        return;
      }

      const topOffset = 112;
      const wrapperTop = desktopTocRef.current.getBoundingClientRect().top + window.scrollY;
      setIsDesktopTocPinned(window.scrollY + topOffset >= wrapperTop);
    };

    updatePinnedState();
    window.addEventListener('scroll', updatePinnedState, { passive: true });
    window.addEventListener('resize', updatePinnedState);

    return () => {
      window.removeEventListener('scroll', updatePinnedState);
      window.removeEventListener('resize', updatePinnedState);
    };
  }, [headings, loading, post]);

  const handleTocItemClick = (id) => scrollToElement(id);
  const handleEdit = () => setDialogOpen(true);
  const handleDelete = async () => {
    setDeleteDialogOpen(false);
    try {
      await deleteDocumentationPost(post.id);
      navigate('/documentation');
    } catch (err) {
      console.error('Error deleting documentation post:', err);
      setError('Failed to delete the post');
    }
  };
  const handleDialogClose = (refresh = false) => {
    setDialogOpen(false);
    if (refresh) {
      setLoading(true);
      const fetchPromise = routeName
        ? getDocumentationPostByRoute(routeName)
        : (postId && !isNaN(postId))
          ? getDocumentationPost(postId)
          : getDocumentationPostByRoute(postId);

      fetchPromise
        .then(data => {
          setPost(data);
          if (data?.content) setHeadings(extractHeadings(data.content));
        })
        .catch(err => {
          console.error('Error reloading documentation post:', err);
          setError('Failed to reload the post');
        })
        .finally(() => setLoading(false));
    }
  };
  const navigateBack = () => navigate('/documentation');

  if (loading) return <LoadingView />;
  if (error || !post) return <ErrorView error={error} navigateBack={navigateBack} />;

  const formattedDate = format(new Date(post.published_at), 'MMMM d, yyyy h:mm a');
  const markdownComponents = createDocumentationMarkdownComponents(isDarkMode);

  return (
    <div className="box-border w-full max-w-full px-4 sm:px-6 md:px-8 py-8 overflow-x-hidden">
      <button
        onClick={navigateBack}
        className="flex items-center gap-2 mb-8 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
      >
        <BackIcon />
        <span>Back to Documentation</span>
      </button>

      <PostHeader
        post={post}
        isAdmin={isAdmin}
        onEdit={handleEdit}
        onDeleteClick={() => setDeleteDialogOpen(true)}
        formattedDate={formattedDate}
      />

      {/* Mobile TOC */}
      {headings.length > 0 && (
        <div className="block lg:hidden mb-6 w-full max-w-full overflow-hidden">
          <div className="bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-sm border border-ui-light-border dark:border-ui-dark-border overflow-hidden">
            <div className="flex items-center gap-2 p-4 border-b border-ui-light-border dark:border-ui-dark-border">
              <TocIcon />
              <h2 className="text-lg font-semibold text-ui-light-text-primary dark:text-ui-dark-text-primary">Contents</h2>
            </div>
            <div className="max-h-48 overflow-y-auto p-2">
              {headings.map((heading, index) => (
                <div
                  key={index}
                  onClick={() => handleTocItemClick(heading.id)}
                  className="px-3 py-2 cursor-pointer rounded-lg hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors"
                  style={{ paddingLeft: `${heading.level * 1.5}rem` }}
                >
                  <span className={`text-sm truncate block ${
                    heading.level === 1
                      ? 'text-primary-600 dark:text-primary-400 font-medium'
                      : 'text-ui-light-text-primary dark:text-ui-dark-text-primary'
                  }`}>
                    {heading.text}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Layout: Sidebar + Content */}
      <div className="flex flex-col gap-6 w-full max-w-full lg:grid lg:grid-cols-[16rem_minmax(0,1fr)] lg:items-start lg:gap-8">
        {/* Desktop TOC Sidebar */}
        {headings.length > 0 && (
          <aside ref={desktopTocRef} className="hidden lg:block lg:w-64 lg:flex-shrink-0">
            <div
              className={isDesktopTocPinned
                ? 'lg:fixed lg:top-28 lg:w-64 lg:max-h-[calc(100vh-9rem)] lg:overflow-y-auto'
                : 'lg:w-64'
              }
            >
              <TableOfContents headings={headings} onItemClick={handleTocItemClick} />
            </div>
          </aside>
        )}

        {/* Main Content */}
        <article
          ref={contentRef}
          className="min-w-0 max-w-full prose prose-sm sm:prose lg:prose-lg dark:prose-invert"
        >
          <ReactMarkdown
            components={markdownComponents}
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeSlug, rehypeRaw]}
          >
            {post.content}
          </ReactMarkdown>
        </article>
      </div>

      <DocumentationPostDialog open={dialogOpen} onClose={handleDialogClose} post={post} />

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default DocumentationDetail;
